import {
  Component,
  HostBinding,
  OnDestroy,
  OnInit,
  ViewEncapsulation,
} from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { TreoMediaWatcherService } from '@treo/services/media-watcher';
import {
  TreoNavigationItem,
  TreoNavigationService,
} from '@treo/components/navigation';
import { defaultNavigation } from '@layout/layout-data';
import { MatDialogService } from '@services/dialogs.service';
import { TreoAnimations } from '@treo/animations';
import { DevCycleService } from '@services/dev-cycle.service';
import { cloneNavigationItems } from '@utils/deep-clone.utils';

@Component({
  selector: 'classic-layout',
  templateUrl: './classic.component.html',
  styleUrls: ['./classic.component.scss'],
  animations: TreoAnimations,
  encapsulation: ViewEncapsulation.None,
  providers: [MatDialogService],
})
export class ClassicLayoutComponent implements OnInit, OnDestroy {
  isScreenSmall: boolean;

  @HostBinding('class.fixed-header')
  fixedHeader: boolean;

  @HostBinding('class.fixed-footer')
  fixedFooter: boolean;

  defaultNavigation: TreoNavigationItem[] = defaultNavigation;

  // Private
  private _unsubscribeAll: Subject<any>;

  /**
   * Constructor
   *
   * @param {TreoMediaWatcherService} _treoMediaWatcherService
   * @param {TreoNavigationService} _treoNavigationService
   */
  constructor(
    private _treoMediaWatcherService: TreoMediaWatcherService,
    private _treoNavigationService: TreoNavigationService,
    public MDS: MatDialogService,
    private devCycle: DevCycleService,
  ) {
    // Set the private defaults
    this._unsubscribeAll = new Subject();

    // Set the defaults
    this.fixedHeader = true;
    this.fixedFooter = true;
    this.isScreenSmall = false;
  }

  // -----------------------------------------------------------------------------------------------------
  // @ Lifecycle hooks
  // -----------------------------------------------------------------------------------------------------

  /**
   * On init
   */
  ngOnInit(): void {
    // Subscribe to media changes
    this._treoMediaWatcherService.onMediaChange$
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe(({ matchingAliases }) => {
        // Check if the breakpoint is 'lt-md'
        this.isScreenSmall = matchingAliases.includes('lt-md');
      });

    // Initialize navigation with feature flags
    this.disableNavigationLinks();
  }

  /**
   * On destroy
   */
  ngOnDestroy(): void {
    // Unsubscribe from all subscriptions
    this._unsubscribeAll.next(null);
    this._unsubscribeAll.complete();
  }

  // -----------------------------------------------------------------------------------------------------
  // @ Public methods
  // -----------------------------------------------------------------------------------------------------

  /**
   * Toggle navigation
   *
   * @param key
   */
  toggleNavigation(key: string): void {
    // Get the navigation
    const navigation = this._treoNavigationService.getComponent(key);

    if (navigation) {
      // Toggle the opened status
      navigation.toggle();
    }
  }

  private removeLinkSideBar(
    navigationLinks: TreoNavigationItem[],
    linkPath: string,
  ): void {
    const entryIndex = navigationLinks.findIndex((entry) =>
      entry.children?.some((child) => child.id?.includes(linkPath)),
    );
    const childIndex = navigationLinks[entryIndex]?.children?.findIndex(
      (child) => child.id?.includes(linkPath),
    );

    if (entryIndex !== -1 && childIndex !== -1) {
      navigationLinks[entryIndex]?.children?.splice(childIndex as number, 1);
    }
  }

  async disableNavigationLinks() {
    console.log('disableNavigationLinks');
    // Create a deep copy of the navigation array using robust deep clone
    const navigationLinks = cloneNavigationItems(defaultNavigation);

    // Get all feature flags
    const storybook = await this.devCycle.getVariable('ew-storybook', false);
    const users = await this.devCycle.getVariable(
      'ew-941-multi-admin-users',
      false,
    );
    const customSpread = await this.devCycle.getVariable(
      'ew-2830-custom-spread',
      false,
    );
    const commissions = await this.devCycle.getVariable(
      'ew-2729-commission-payment-request',
      false,
    );

    console.log('Feature flags:', {
      storybook,
      users,
      customSpread,
      commissions,
    });

    // Remove storybook if flag is disabled
    if (!storybook) {
      const index = navigationLinks.findIndex(
        (entry: TreoNavigationItem) => entry.id === 'storybook',
      );
      if (index >= 0) {
        navigationLinks.splice(index, 1);
      }
    }

    // Remove users if flag is disabled
    if (!users) {
      this.removeLinkSideBar(navigationLinks, 'users');
    }

    // Remove custom spread if flag is disabled
    if (!customSpread) {
      this.removeLinkSideBar(navigationLinks, 'custom_spread');
    }

    // Remove commissions if flag is disabled
    if (!commissions) {
      // Find and remove the commissions menu item
      const commissionsIndex = navigationLinks.findIndex(
        (entry: TreoNavigationItem) => entry.id === 'comissions',
      );
      if (commissionsIndex >= 0) {
        navigationLinks.splice(commissionsIndex, 1);
      }

      // Also remove the COMMISSION group header (sidebar:group2)
      const groupIndex = navigationLinks.findIndex(
        (entry: TreoNavigationItem) =>
          entry.type === 'group' && entry.title === 'sidebar:group2',
      );
      if (groupIndex >= 0) {
        navigationLinks.splice(groupIndex, 1);
      }
    }

    this.defaultNavigation = navigationLinks;
  }
}
